import React, { useState, useEffect } from 'react';
import api from '../api/api';
import '../styles/UserManagement.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Estado del formulario de usuario
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    rol: 'vendedor', // 'administrador', 'vendedor', 'gerente'
    password: '',
  });

  // Obtener usuarios
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/users');
      setUsers(res.data);
    } catch (error) {
      console.error('Error al obtener usuarios:', error);
      alert('No se pudieron cargar los usuarios.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Abrir modal para agregar nuevo usuario
  const openModalToAdd = () => {
    setEditingUser(null);
    setFormData({
      nombre: '',
      email: '',
      rol: 'vendedor',
      password: '',
    });
    setShowModal(true);
  };

  // Abrir modal para editar usuario
  const openModalToEdit = (user) => {
    setEditingUser(user);
    setFormData({
      nombre: user.nombre,
      email: user.email || '',
      rol: user.rol || 'vendedor',
      password: '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Crear o actualizar usuario
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre || !formData.email || (!editingUser && !formData.password)) {
      alert('Completa los campos obligatorios: Nombre, Email y Contraseña (para nuevos usuarios).');
      return;
    }

    try {
      if (editingUser) {
        // Si está editando y deja password en blanco, NO lo mandamos
        const payload =
          formData.password.trim() === ''
            ? { nombre: formData.nombre, email: formData.email, rol: formData.rol }
            : { ...formData };

        await api.put(`/api/users/${editingUser.id}`, payload);
        alert('Usuario actualizado correctamente.');
      } else {
        await api.post('/api/users', formData);
        alert('Usuario creado correctamente.');
      }

      closeModal();
      fetchUsers();
    } catch (error) {
      console.error('Error al guardar usuario:', error);
      alert('Ocurrió un error al guardar el usuario.');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('¿Está seguro de eliminar este usuario?')) return;

    try {
      await api.delete(`/api/users/${userId}`);
      alert('Usuario eliminado.');
      fetchUsers();
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      alert('No se pudo eliminar el usuario.');
    }
  };

  if (loading) {
    return (
      <div className="um-container">
        <p>Cargando usuarios...</p>
      </div>
    );
  }

  return (
    <div className="um-container">
      <h1>Administración de Usuarios</h1>
      <p>Esta sección es accesible solo para administradores.</p>

      <div className="um-actions no-print">
        <button className="um-add-button" onClick={openModalToAdd}>
          Añadir Usuario
        </button>
      </div>

      <div className="um-table-container">
        {users.length === 0 ? (
          <p>No hay usuarios registrados.</p>
        ) : (
          <table className="um-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Email</th>
                <th>Rol</th>
                <th className="no-print">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.nombre}</td>
                  <td>{user.email}</td>
                  <td>{user.rol === 'administrador' ? 'Administrador' : user.rol}</td>
                  <td className="no-print">
                    <button className="um-edit-button" onClick={() => openModalToEdit(user)}>
                      Editar
                    </button>
                    <button className="um-delete-button" onClick={() => handleDelete(user.id)}>
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="um-modal-overlay no-print" onMouseDown={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="um-modal">
            <h2>{editingUser ? 'Editar Usuario' : 'Añadir Usuario'}</h2>

            <form onSubmit={handleSubmit} className="um-form">
              <label>
                Nombre *:
                <input type="text" name="nombre" value={formData.nombre} onChange={handleChange} required />
              </label>

              <label>
                Email *:
                <input type="email" name="email" value={formData.email} onChange={handleChange} required />
              </label>

              <label>
                Rol:
                <select name="rol" value={formData.rol} onChange={handleChange}>
                  <option value="administrador">Administrador</option>
                  <option value="vendedor">Vendedor</option>
                  <option value="gerente">Gerente</option>
                </select>
              </label>

              <label>
                Contraseña {editingUser ? '(dejar en blanco para no cambiar)' : '*'}:
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required={!editingUser}
                />
              </label>

              <div className="um-form-actions">
                <button type="submit">{editingUser ? 'Actualizar' : 'Crear'}</button>
                <button type="button" onClick={closeModal}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
